<template>
  <div>
    <extraction-template-insert-row
      :disabled="disableAdd"
      @add-header="(closeRow) => onAddHeader(null, closeRow)"
      @add-field="(closeRow) => onAddField(null, closeRow)"
    />
    <draggable-panels
      item-key="id"
      :draggable-props="draggableProps"
      :open.sync="openFields"
      :items="fieldItems"
      @update:items="onUpdateFieldOrder"
    >
      <template #default="{ item, index, key, isActive }">
        <div :key="key">
          <div class="etp-drag-row" :class="{ 'no-drag': isActive }">
            <div
              class="etp-drag-handle my-4"
              :class="{
                disable: isActive,
                'is-header': item.isHeader,
              }"
            >
              <v-icon class="grey--text text--lighten-1">
                reorder
              </v-icon>
            </div>
            <extraction-template-header-item
              v-if="item.isHeader"
              :key="key"
              class="etp-in-front etp-header"
              :value="item.localField"
              :is-active="isActive"
              :item-style="item.fieldStyle"
              @input="onUpdateField"
              @delete-field="onDeleteField(item.id)"
              @revert-field="onRevertField(item.id)"
            />
            <expansion-panel
              v-else
              :key="key"
              class="etp-in-front etp-field"
              :value="isActive"
              :item-value="key"
            >
              <template #activator>
                <extraction-template-list-item
                  :key="key"
                  :value="item.localField"
                  :item-style="item.fieldStyle"
                  @delete-field="onDeleteField(item.id)"
                  @revert-field="onRevertField(item.id)"
                />
              </template>
              <template #default="{ toggle }">
                <extraction-template-field-item
                  :key="key"
                  :value="item.localField"
                  :is-active="isActive"
                  :lock-type="!!item.existingField"
                  :item-style="item.fieldStyle"
                  class="px-6 pt-4"
                  @input="onUpdateField"
                  @delete-field="onDeleteField(item.id)"
                  @revert-field="onRevertField(item.id)"
                  @close="toggle"
                />
              </template>
            </expansion-panel>
            <extraction-template-insert-row
              :key="`insert-row-${key}`"
              class="etp-behind etp-insert-row"
              :disabled="disableAdd"
              :always-open="index + 1 === fieldItems.length"
              @add-header="(closeCb) => onAddHeader(item.id, closeCb)"
              @add-field="(closeCb) => onAddField(item.id, closeCb)"
            />
          </div>
        </div>
      </template>
    </draggable-panels>
  </div>
</template>

<script>
import ExtractionTemplateFieldItem from './ExtractionTemplateFieldItem.vue';
import ExtractionTemplateHeaderItem from './ExtractionTemplateHeaderItem.vue';
import ExtractionTemplateInsertRow from './ExtractionTemplateInsertRow.vue';
import ExtractionTemplateListItem from './ExtractionTemplateListItem.vue';
import DraggablePanels from '@/components/Common/DraggablePanels.vue';
import ExpansionPanel from '@/components/Common/ExpansionPanel.vue';
import {
  createDataExtractionField,
  DataExtractionFieldHeader,
  DataExtractionFieldDraft,
} from '@/models';
import { isArrayOfType } from '@/modules/typeHelpers';
import { deepEqualExcludingId } from '@/modules/compareContents';
import { ExtractionTemplateItemStyle } from '@/enums';

export default {
  name: 'ExtractionTemplatePanel',
  components: {
    ExtractionTemplateFieldItem,
    ExtractionTemplateHeaderItem,
    ExtractionTemplateInsertRow,
    ExtractionTemplateListItem,
    DraggablePanels,
    ExpansionPanel,
  },
  props: {
    fieldDrafts: {
      type: Array,
      required: true,
      validator: (val) =>
        isArrayOfType(val, DataExtractionFieldDraft),
    },
    disableAdd: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'add-heading',
    'add-field',
    'update-fields',
    'revert-field',
    'delete-field',
  ],
  data: () => ({
    fieldItems: [],
    openFields: [],
  }),
  computed: {
    draggableProps() {
      return {
        move: this.checkMoveCallback,
        filter: '.no-drag',
        handle: '.etp-drag-handle',
        preventOnFilter: false,
      };
    },
  },
  watch: {
    fieldDrafts: {
      handler(newFieldDrafts) {
        const localFieldsByDraftId = new Map(
          this.fieldItems.map((item) => [item.id, item.localField])
        );

        const newFieldItems = [];
        for (const fieldDraft of newFieldDrafts) {
          const {
            id,
            updatedField,
            existingField,
            modified,
          } = fieldDraft;
          const activeField = updatedField ?? existingField;
          if (!activeField) {
            continue;
          }
          const existingLocalField = localFieldsByDraftId.get(
            activeField.id
          );
          const localField =
            existingLocalField ||
            createDataExtractionField(activeField);

          const isNew = !existingField;
          const isDeleted = modified && !updatedField;
          const isModified =
            existingField &&
            updatedField &&
            !deepEqualExcludingId(existingField, updatedField);

          const isHeader =
            localField instanceof DataExtractionFieldHeader;
          const fieldStyle =
            (isDeleted && ExtractionTemplateItemStyle.DELETED) ||
            (isNew && ExtractionTemplateItemStyle.NEW) ||
            (isModified && ExtractionTemplateItemStyle.MODIFIED) ||
            ExtractionTemplateItemStyle.DEFAULT;

          if (localField || existingField) {
            newFieldItems.push({
              id,
              localField,
              existingField,
              isHeader,
              fieldStyle,
            });
          }
        }

        this.fieldItems = newFieldItems;
        this.openEmptyFields();
      },
      immediate: true,
    },
  },
  methods: {
    onUpdateField(newField) {
      const fieldToUpdate = this.fieldItems.find(
        (x) => x.localField.id === newField.id
      );
      Object.assign(fieldToUpdate.localField, newField);

      let newFields = this.fieldItems.map((item) => item.localField);
      this.$emit('update-fields', newFields);
    },
    onAddHeader(afterDraftId, closeRow) {
      this.openFields = [];
      this.$emit('add-heading', {
        afterDraftId,
        closeRow,
      });
    },
    onAddField(afterDraftId, closeRow) {
      this.openFields = [];
      this.$emit('add-field', {
        afterDraftId,
        closeRow,
      });
    },
    onDeleteField(draftId) {
      const itemIdxToUpdate = this.fieldItems.findIndex(
        (x) => x.id === draftId
      );
      const existingItem = this.fieldItems[itemIdxToUpdate];
      if (existingItem.existingField) {
        const newItem = {
          ...existingItem,
          fieldStyle: ExtractionTemplateItemStyle.DELETED,
        };
        this.fieldItems.splice(itemIdxToUpdate, 1, newItem);
      } else {
        this.fieldItems.splice(itemIdxToUpdate, 1);
      }
      this.$emit('delete-field', draftId);
    },
    onRevertField(draftId) {
      const itemIdxToUpdate = this.fieldItems.findIndex(
        (x) => x.id === draftId
      );
      const existingItem = this.fieldItems[itemIdxToUpdate];
      const existingField = existingItem.existingField;
      if (existingField) {
        const newItem = {
          ...existingItem,
          localField: existingItem.existingField,
          fieldStyle: ExtractionTemplateItemStyle.DEFAULT,
        };
        this.fieldItems.splice(itemIdxToUpdate, 1, newItem);
      } else {
        this.fieldItems.splice(itemIdxToUpdate, 1);
      }
      this.$emit('revert-field', draftId);
    },
    onUpdateFieldOrder(newItems) {
      this.fieldItems = newItems;
      let newFields = this.fieldItems.map((item) => item.localField);
      this.$emit('update-fields', newFields);
    },
    openEmptyFields() {
      for (const field of this.fieldItems) {
        if (field.localField.title.length === 0) {
          if (!this.openFields.includes(field)) {
            this.openFields.push(field);
            return;
          }
        }
      }
    },
    checkMoveCallback(evt) {
      const draggedField = evt.draggedContext.element;
      if (this.openFields.includes(draggedField)) {
        return false;
      }
      return true;
    },
  },
};
</script>
<style lang="scss" scoped>
.etp-behind {
  z-index: 1;
}
.etp-in-front {
  z-index: 2;
}

.etp-drag-row {
  display: grid;
  grid:
    'handle content' auto
    '_      insert' auto
    / auto 1fr;
}

.etp-drag-handle {
  grid-area: handle;
  margin-right: 1rem;
  cursor: grab;

  align-self: start;
  &.is-header {
    align-self: center;
  }

  &.disable {
    cursor: not-allowed;
  }
}

.etp-etp-header {
  grid-area: content;
}

.etp-etp-field {
  grid-area: content;
}

.etp-insert-row {
  grid-area: insert;
}
</style>
